import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiShield, FiSlash, FiTrash2, FiEye, FiRefreshCw, FiX } from 'react-icons/fi';
import UserService from '../services/UserService';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detailUser, setDetailUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await UserService.getAllUsers(1, 200, search);
      const list = Array.isArray(res) ? res : (res.users || res.data || res.customers || []);
      setUsers(list);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(() => loadUsers(), 300);
    return () => clearTimeout(timeout);
  }, [loadUsers]);

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'banned' ? 'active' : 'banned';
    try {
      await UserService.toggleUserStatus(id, newStatus);
      loadUsers();
    } catch (err) { alert('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await UserService.deleteUser(id);
      loadUsers();
    } catch (err) { alert('Failed to delete user'); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await UserService.getUserDetails(id);
      setDetailUser(res.user || res);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-5 max-w-7xl text-left">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Users Management</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">{users.length} registered accounts</p>
        </div>
        <button onClick={loadUsers} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh"><FiRefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="relative">
        <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or username..."
          className="w-full max-w-md bg-white border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 transition shadow-sm"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-20"><p className="text-sm font-bold text-slate-500">No users found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/70">
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Username</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">City</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">XP</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-3 text-right text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-700 text-white flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden shadow-sm">
                          {u.image ? <img src={u.image} alt="" className="w-full h-full object-cover" /> : (u.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">{u.name || '-'}</p>
                          <p className="text-[10px] text-slate-500 font-medium">{u.email || u.phone || '-'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium text-slate-600">@{u.username || '-'}</td>
                    <td className="px-4 py-3.5 text-xs font-medium text-slate-600">{u.city || u.geoCity || '-'}</td>
                    <td className="px-4 py-3.5 text-xs text-purple-700 font-bold">{u.xpPoints || u.xp || 0}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-extrabold border ${u.status === 'banned' ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                        {u.status === 'banned' ? 'Banned' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium text-slate-500">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => loadDetail(u._id)} className="p-1.5 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition"><FiEye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleToggleStatus(u._id, u.status)} className="p-1.5 rounded-lg text-slate-500 hover:text-amber-700 hover:bg-amber-50 transition" title={u.status === 'banned' ? 'Unban' : 'Ban'}>
                          {u.status === 'banned' ? <FiShield className="w-3.5 h-3.5" /> : <FiSlash className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => handleDelete(u._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"><FiTrash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {detailUser && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setDetailUser(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-lg space-y-4 text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-700 text-white flex items-center justify-center font-bold text-base overflow-hidden shadow-sm">
                  {detailUser.image ? <img src={detailUser.image} alt="" className="w-full h-full object-cover" /> : (detailUser.name || 'U').charAt(0)}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{detailUser.name}</h3>
                  <p className="text-xs text-slate-500 font-medium">@{detailUser.username}</p>
                </div>
              </div>
              <button onClick={() => setDetailUser(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Detail label="Email" value={detailUser.email} />
              <Detail label="Phone" value={detailUser.phone} />
              <Detail label="City" value={detailUser.city || detailUser.geoCity} />
              <Detail label="XP Points" value={detailUser.xpPoints || detailUser.xp || 0} />
              <Detail label="Status" value={detailUser.status || 'active'} />
              <Detail label="Joined" value={detailUser.createdAt ? new Date(detailUser.createdAt).toLocaleDateString() : '-'} />
            </div>

            <div className="pt-2 border-t border-slate-100">
              <button onClick={() => setDetailUser(null)} className="w-full py-2.5 bg-slate-100 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-200 transition">Close Profile</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-slate-500 font-bold uppercase">{label}</p>
    <p className="text-xs text-slate-900 font-semibold mt-0.5">{value || '-'}</p>
  </div>
);

export default Users;
